import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    url: String,
    page: Number,
    hasMore: Boolean
  }
  
  static targets = ["sentinel"]

  connect() {
    this.loading = false
    this.setupObserver()
    
    // Listen for value updates from Turbo Stream
    this.element.addEventListener('infinite-scroll:values-updated', (event) => {
      // Read values from data attributes (Stimulus should update, but read directly to be sure)
      const pageValue = parseInt(this.element.dataset.infiniteScrollPageValue) || event.detail.page
      const hasMoreValue = this.element.dataset.infiniteScrollHasMoreValue === 'true' || event.detail.hasMore
      
      this.pageValue = pageValue
      this.hasMoreValue = hasMoreValue
      
      // Re-setup observer after values update
      if (this.observer) {
        this.observer.disconnect()
      }
      this.setupObserver()
    })
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }

  setupObserver() {
    // Disconnect existing observer if any
    if (this.observer) {
      this.observer.disconnect()
    }
    
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this.hasMoreValue && !this.loading) {
            this.loadMore()
          }
        })
      },
      {
        rootMargin: "200px" // Start loading 200px before reaching the bottom
      }
    )

    // Wait a bit for DOM to settle, then observe sentinel
    setTimeout(() => {
      if (this.hasSentinelTarget) {
        this.observer.observe(this.sentinelTarget)
      }
    }, 100)
  }

  loadMore() {
    if (this.loading || !this.hasMoreValue) {
      return
    }
    
    this.loading = true

    const nextPage = this.pageValue + 1
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("page", nextPage)

    fetch(url, {
      headers: {
        Accept: "text/vnd.turbo-stream.html",
        "X-Requested-With": "XMLHttpRequest"
      }
    })
      .then((response) => response.text())
      .then((html) => {
        Turbo.renderStreamMessage(html)
        // Wait for Turbo Stream to process and update DOM
        setTimeout(() => {
          // Read updated values from the hidden element added by Turbo Stream
          const updateElement = document.getElementById('infinite-scroll-values-update')
          if (updateElement) {
            const pageValue = parseInt(updateElement.dataset.infiniteScrollPage) || nextPage
            const hasMoreValue = updateElement.dataset.infiniteScrollHasMore === 'true'
            
            this.pageValue = pageValue
            this.hasMoreValue = hasMoreValue
            
            // Update data attributes on the main container for Stimulus
            this.element.setAttribute('data-infinite-scroll-page-value', pageValue)
            this.element.setAttribute('data-infinite-scroll-has-more-value', hasMoreValue)
            
            // Remove the update element
            updateElement.remove()
          } else {
            // Fallback: use nextPage if update element not found
            this.pageValue = nextPage
            this.element.setAttribute('data-infinite-scroll-page-value', nextPage)
          }
          
          // Re-observe the new sentinel if it exists
          // Disconnect and reconnect observer to pick up new sentinel element
          if (this.observer) {
            this.observer.disconnect()
          }
          this.setupObserver()
          this.loading = false
        }, 400)
      })
      .catch((error) => {
        console.error("Error loading more pages:", error)
        this.loading = false
      })
  }
}

